import type { FilesRailFileMenuActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailFileRow";
import { useFileRecordCardActions } from "@/pages/workspaces/hooks/useFileRecordCardActions";
import type { FileRecord } from "@cohesive/api-client";
import { useCallback, useMemo } from "react";

type UseFilesRailContextFileActionsParams = {
  allRecords: FileRecord[];
  /** When set, placed canvas files have their node removed before delete. */
  fileRecordIdToNodeId?: Map<string, string>;
};

/** Menu actions + dialogs for canvas / flow-session file rows in the Files rail context section. */
export function useFilesRailContextFileActions({
  allRecords,
  fileRecordIdToNodeId,
}: UseFilesRailContextFileActionsParams) {
  const { dialogs: baseDialogs, actions } = useFileRecordCardActions({
    allRecords,
    onFolderOpen: () => {},
  });

  const handleDelete = useCallback(
    (record: FileRecord) => {
      const nodeId = fileRecordIdToNodeId?.get(record.id);
      if (nodeId) {
        window.dispatchEvent(
          new CustomEvent("canvas:remove-node", {
            detail: { nodeId, origin: "no-undo:file-delete" },
          }),
        );
      }
      actions.delete(record);
    },
    [actions.delete, fileRecordIdToNodeId],
  );

  const menuActions = useMemo(
    (): FilesRailFileMenuActions => ({
      onRename: actions.rename,
      onDelete: handleDelete,
      onCopy: actions.copy,
    }),
    [actions.rename, actions.copy, handleDelete],
  );

  return { menuActions, dialogs: baseDialogs };
}
